import React from 'react';
import Page from '../../containers/Page/Page';
import { Box, Button, IconButton, Theme, Typography, createStyles, makeStyles, Grid, TextField, MenuItem, ListItem, ListItemAvatar, Avatar, ListItemText, Card, CardActions, Dialog, DialogTitle, DialogContent, DialogActions, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, CircularProgress } from '@material-ui/core';
import { useHistory, useParams } from 'react-router-dom';
import { ArrowBack, AssignmentInd, Save } from '@material-ui/icons';
import { useSnackbar } from 'notistack';
import { Helpers } from '../../utils/Helpers';
import { api } from '../../services/api';

interface IUsuario {
    nome: string;
    email: string;
}

interface IPapel {
    id: string;
    nome: string;
    descricao: string;
}

interface IPermissao {
    id: string;
    descricao: string;
}

interface IParams {
    id: string;
}

function UsuariosCreate() {

    const history = useHistory();
    const classes = useStyles();
    const params = useParams<IParams>();

    const { enqueueSnackbar: alert } = useSnackbar();

    const [error, setError] = React.useState<string>('');
    const [loading, setLoading] = React.useState<boolean>(false);
    const [usuario, setUsuario] = React.useState<IUsuario>({} as IUsuario);
    const [papel, setPapel] = React.useState<string>('');
    const [papeis, setPapeis] = React.useState<IPapel[]>([]);
    const [permissoes, setPermissoes] = React.useState<IPermissao[]>([]);
    const [permissoesDialog, setPermissoesDialog] = React.useState<boolean>(false);
    const [permissoesLoading, setPermissoesLoading] = React.useState<boolean>(false);

    React.useEffect(() => {
        handleFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        handleFetchPapeis();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function handleFetch() {
        setLoading(true);
        try {
            const response = await api.get(`usuarios/${params.id}`);
            setUsuario(response.data.object);
            setPapel(response.data.object.papel[0] ? response.data.object.papel[0].id : '');
            setError('');
        } catch (error: any) {
            setError(Helpers.errorFormat(error));
        }
        setLoading(false);
    }

    async function handleFetchPapeis() {
        setLoading(true);
        try {
            const response = await api.get('papeis');
            setPapeis(response.data.object);
            setError('');
        } catch (error: any) {
            setError(Helpers.errorFormat(error));
        }
        setLoading(false);
    }

    async function handleFetchPermissoes(papel_id: string) {
        setPermissoesDialog(true);
        setPermissoesLoading(true);
        try {
            const response = await api.get(`papeis/permissoes/${papel_id}`);
            setPermissoes(response.data.object);
        } catch (error: any) {
            Helpers.error(error, alert);
            setPermissoesDialog(false);
        }
        setPermissoesLoading(false);
    }

    async function handleSave() {
        setLoading(true);
        try {
            const response = await api.put(`usuarios/${params.id}`, { papel_id: papel });
            alert(response.data.message, { variant: 'success' });
        } catch (error) {
            Helpers.error(error, alert);
        }
        setLoading(false);
    }

    return (
        <Page error={error} loading={loading}>
            <Page.Title>
                <Box display="flex" flexDirection="row" alignItems="center">
                    <IconButton size="small" color="primary" className={classes.backButton} onClick={() => history.goBack()}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h6" component="div">
                        Editar Usuário
                    </Typography>
                </Box>
                <Button
                    color="primary"
                    variant="contained"
                    startIcon={<Save />}
                    onClick={() => handleSave()}>
                    Salvar
                </Button>
            </Page.Title>
            <Page.Section>
                <Page.Content>
                    <Box p={2}>
                        <Grid className={classes.row} container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    id="nome"
                                    name="nome"
                                    label="Nome"
                                    variant="outlined"
                                    fullWidth={true}
                                    disabled={true}
                                    value={usuario.nome || ''}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    id="email"
                                    name="email"
                                    label="Email"
                                    variant="outlined"
                                    fullWidth={true}
                                    disabled={true}
                                    value={usuario.email || ''}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    id="papel"
                                    name="papel"
                                    label="Papel"
                                    variant="outlined"
                                    fullWidth={true}
                                    select={true}
                                    value={papel || ''}
                                    onChange={(event) => setPapel(event.target.value)}
                                >
                                    {papeis.map((papel, index) =>
                                        <MenuItem key={index} value={papel.id}>{papel.nome}</MenuItem>
                                    )}
                                </TextField>
                            </Grid>
                        </Grid>
                        <Grid container spacing={2}>
                            {papeis.map((papel, index) => (
                                <Grid key={index} item xs={12} md={4}>
                                    <Card variant="outlined">
                                        <ListItem>
                                            <ListItemAvatar>
                                                <Avatar>
                                                    <AssignmentInd />
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText primary={papel.nome} secondary={papel.descricao} />
                                        </ListItem>
                                        <CardActions>
                                            <Button size="small" color="primary" onClick={() => handleFetchPermissoes(papel.id)}>
                                                Ver Permissões
                                            </Button>
                                        </CardActions>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                        <Dialog open={permissoesDialog} fullWidth={true} maxWidth="xs" onClose={() => setPermissoesDialog(false)}>
                            <DialogTitle>Permissões</DialogTitle>
                            <DialogContent>
                                {permissoesLoading
                                    ?
                                    <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center">
                                        <CircularProgress color="secondary" />
                                    </Box>
                                    :
                                    <TableContainer>
                                        <Table stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Permissão</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {permissoes && permissoes.map((permissao, index) =>
                                                    <TableRow key={index}>
                                                        <TableCell>{permissao.descricao}</TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                }
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setPermissoesDialog(false)} color="primary" autoFocus>
                                    Fechar
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </Box>
                </Page.Content>
            </Page.Section>
        </Page>
    );
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        backButton: {
            marginRight: theme.spacing(1),
        },
        row: {
            marginBottom: theme.spacing(1)
        },
        custom: {
            padding: '2px 4px',
            display: 'flex',
            alignItems: 'center',
        },
        input: {
            marginLeft: theme.spacing(1),
            flex: 1,
        },
    })
);

export default UsuariosCreate;